import { useState, useMemo } from "react";
import {
  Package,
  MapPin,
  Banknote,
  Hash,
  Layers,
  ChevronRight,
  ChevronLeft,
  Activity,
  ShieldCheck,
  Tag
} from "lucide-react";
import { PartDto } from "@/types";
import { useNavigation } from "@/context/NavigationContext";
import { useAuth } from "@/context/AuthContext";
import { PART_STATES, PART_CATEGORIES } from "../enums";

interface AircraftPartCardProps {
  part: PartDto;
}

// 🟢 Funcție utilitară pentru a detecta valori nule, nedefinite sau placeholder-e de tip "N/A"
const isInvalidValue = (value: any): boolean => {
  if (value === null || value === undefined) return true;
  const str = String(value).trim().toLowerCase();
  return str === "" || str === "null" || str === "undefined" || str === "n/a" || str === "na";
};

const AircraftPartCard = ({ part }: AircraftPartCardProps) => {
  const { navigateTo } = useNavigation();
  const { myAircraftParts } = useAuth();
  const [currentIndex, setCurrentIndex] = useState(0);

  const isOwnListing = useMemo(() => {
    if (!myAircraftParts || !part.partId) return false;
    return myAircraftParts.some((myPart) => myPart.partId === part.partId);
  }, [myAircraftParts, part.partId]);

  const images = (() => {
      if (part.mediaUrls && part.mediaUrls.length > 0) {
        return part.mediaUrls.filter(url => url && url.trim() !== "");
      }

      if (part.media && part.media.length > 0) {
        return part.media.map((m) => {
          const data = (m as any).mediaData;
          if (!data) return "";
          return data.startsWith("data:") ? data : `data:image/jpeg;base64,${data}`;
        }).filter(img => img !== "");
      }

      return [`https://via.placeholder.com/400x300?text=${encodeURIComponent(part.title || "Part")}`];
    })();

  const formatPrice = (price?: number) => {
    if (!price || isInvalidValue(price)) return "LA CERERE";
    return new Intl.NumberFormat("ro-RO", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0
    }).format(price);
  };

  const handleCardClick = () => {
    navigateTo("part-details", { partId: part.partId, part: part, seller: isOwnListing ? 1 : 0});
  };

  const nextImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const prevImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  // Rezolvare label-uri
  const currentState = PART_STATES.find(s => s.code === part.state);
  const rawState = currentState ? currentState.label : part.state;
  const stateLabel = isInvalidValue(rawState) ? null : rawState;

  const currentCategory = PART_CATEGORIES.find(c => c.code === part.category);
  const rawCategory = currentCategory ? currentCategory.label : part.category;
  const categoryLabel = isInvalidValue(rawCategory) ? null : rawCategory;

  const cleanTitle = isInvalidValue(part.title) ? "Piesă Schimb" : part.title;

  const hudItems = useMemo(() => {
      const items = [];
      if (part.serialNumber && !isInvalidValue(part.serialNumber)) {
        items.push({ id: "sn", icon: Hash, value: part.serialNumber, label: "S/N" });
      }
      if (part.quantity !== undefined && part.quantity !== null && part.quantity > 0) {
        items.push({ id: "stock", icon: Layers, value: part.quantity, label: "Stock" });
      }
      if (stateLabel) {
        items.push({ id: "status", icon: ShieldCheck, value: stateLabel, label: "Status" });
      }
      return items;
    }, [part.serialNumber, part.quantity, stateLabel]);

  return (
    <div
      onClick={handleCardClick}
      className={`group relative bg-[#0F172A]/80 backdrop-blur-xl border rounded-3xl overflow-hidden cursor-pointer hover:border-emerald-500/50 transition-all duration-300 shadow-2xl h-full flex flex-col ${
        isOwnListing ? "border-dashed border-slate-700 opacity-95" : "border-slate-800"
      }`}
    >
      {/* IMAGE SECTION */}
      <div className="relative h-52 overflow-hidden shrink-0">
        <img
          src={images[currentIndex]}
          alt={cleanTitle}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
        />

        {/* Top Badges */}
        <div className="absolute top-3 left-3 flex flex-wrap gap-2 z-10 max-w-[calc(100%-24px)]">
          {isOwnListing && (
            <span className="bg-slate-900/90 backdrop-blur-md border border-orange-500/30 text-orange-400 text-[9px] font-black px-2 py-1 rounded-md uppercase tracking-widest shadow-lg animate-pulse">
              Propria listare
            </span>
          )}
          {/* 🟢 MODIFICAT: Se afișează categoria doar dacă este validă */}
          {categoryLabel && (
            <span className="bg-emerald-600 text-white text-[9px] font-black px-2 py-1 rounded-md uppercase tracking-widest shadow-lg">
              {categoryLabel}
            </span>
          )}
          {/* 🟢 MODIFICAT: Se afișează starea doar dacă este validă */}
          {stateLabel && (
            <span className="bg-slate-900/90 backdrop-blur-md border border-slate-700 text-white text-[9px] font-black px-2 py-1 rounded-md uppercase tracking-widest shadow-lg">
              {stateLabel}
            </span>
          )}
        </div>

        {/* Carousel Controls */}
        {images.length > 1 && (
          <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 flex justify-between px-2 opacity-0 group-hover:opacity-100 transition-opacity z-20">
            <button onClick={prevImage} className="p-1.5 bg-slate-900/80 rounded-full text-white hover:bg-emerald-600 transition-colors">
              <ChevronLeft size={16} />
            </button>
            <button onClick={nextImage} className="p-1.5 bg-slate-900/80 rounded-full text-white hover:bg-emerald-600 transition-colors">
              <ChevronRight size={16} />
            </button>
          </div>
        )}
      </div>

      {/* CONTENT SECTION */}
      <div className="p-5 flex flex-col flex-1 justify-between">
              <div className="space-y-4">
                {/* Title & Manufacturer */}
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Package size={14} className="text-emerald-500" />
                    <h3 className="text-lg font-black text-white tracking-tight uppercase truncate">
                      {cleanTitle}
                    </h3>
                  </div>

                  {/* 🟢 MODIFICAT: Nu se randează dacă manufacturer este null/N/A */}
                  {part.manufacturer && !isInvalidValue(part.manufacturer) && (
                    <div className="flex items-center gap-1 text-slate-500">
                      <Tag size={12} />
                      <span className="text-[11px] font-bold uppercase tracking-wider truncate">
                        {part.manufacturer}
                      </span>
                    </div>
                  )}
                </div>

                {/* HUD Grid */}
                {hudItems.length > 0 && (
                  <div
                    className="grid gap-2 border-y border-slate-800/50 py-3 bg-white/[0.02] rounded-lg"
                    style={{ gridTemplateColumns: `repeat(${hudItems.length}, minmax(0, 1fr))` }}
                  >
                    {hudItems.map((item, index) => {
                      const Icon = item.icon;
                      return (
                        <div
                          key={item.id}
                          className={`text-center ${index > 0 ? "border-l border-slate-800/50" : ""}`}
                        >
                          <Icon size={14} className="mx-auto mb-1 text-slate-400" />
                          <p className="text-[10px] font-black text-white truncate px-1 uppercase">
                            {item.value}
                          </p>
                          <p className="text-[8px] text-slate-500 font-bold uppercase tracking-tighter">
                            {item.label}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Short Description */}
                {/* 🟢 MODIFICAT: Nu se randează dacă descrierea este nulă sau N/A */}
                {part.description && !isInvalidValue(part.description) && (
                  <p className="text-[11px] text-slate-500 line-clamp-2 leading-relaxed">
                    {part.description}
                  </p>
                )}
              </div>

        {/* PRICING FOOTER */}
        <div className="flex items-end justify-between mt-4">
          <div>
            <p className="text-[9px] font-bold text-slate-500 uppercase tracking-[0.2em]">Part Price</p>
            <div className="flex items-baseline gap-1">
              <span className="text-2xl font-black text-white tracking-tighter">
                {formatPrice(part.grossValue)}
              </span>
            </div>
          </div>
          <div className="text-right">
             <button className="bg-emerald-500/10 hover:bg-emerald-500 text-emerald-500 hover:text-white p-2 rounded-xl border border-emerald-500/20 transition-all">
                <ChevronRight size={18} />
             </button>
          </div>
        </div>
      </div>

      {/* DECORATIVE LINE */}
      <div className="absolute bottom-0 left-0 h-1 bg-emerald-600 transition-all duration-300 w-0 group-hover:w-full" />
    </div>
  );
};

export default AircraftPartCard;